import styled, { css } from 'styled-components';
import { HEADER_HEIGHT, SEARCH_WIDGET_HEIGHT } from '@/app/common/constants.ts';
import type { Brand } from '@/domainLogic/index.ts';
import customCss from '@/app/components/hotel-detail/css/customCss.ts';
import { pillTrailingSpace } from '@/app/common/components/Pill/styled.ts';
import { headerHeightCss } from '@/app/common/headerFooter/headerHeight.ts';
import { whenDesktopLayoutIsNarrow } from '@/app/common/css/mixins.ts';
import { isIndexSelector } from '@/app/common/css/selectors.ts';
import { VerticalSeparator } from '@/app/common/components/Separator';

const Z_INDEX_ABOVE_POPOVERS = 4;
const Z_INDEX_BELOW_POPOVERS = 2;

export const HorizontalLayoutContainer = styled.div<{
  brand: Brand;
  mode: 'fixed' | 'sticky';
  showShadow: boolean;
  isActivelySticky: boolean;
  isApp: boolean;
  externalHeaderEnabled: boolean;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  height: 72px;
  z-index: ${({ mode, isActivelySticky }) =>
    mode === 'fixed' || isActivelySticky ? Z_INDEX_ABOVE_POPOVERS : Z_INDEX_BELOW_POPOVERS};
  position: fixed;
  top: ${({ brand, isApp }) => (isApp ? '0px' : headerHeightCss(HEADER_HEIGHT(brand, true)))};
  left: 0;
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
  transition:
    opacity 0.3s ease-in,
    top 0.35s ease-out;

  ${isIndexSelector} & {
    @media (max-width: 767px) {
      display: none;
    }
  }

  ${pillTrailingSpace};

  ${({ mode, externalHeaderEnabled, brand }) =>
    mode === 'fixed' &&
    externalHeaderEnabled &&
    css`
      @media ${customCss.breakpoints.mdPlus} {
        top: ${HEADER_HEIGHT(brand, false) + SEARCH_WIDGET_HEIGHT}px;
      }
    `};

  ${({ mode }) =>
    mode === 'fixed'
      ? css`
          padding-left: 24px;
          padding-right: 16px;
        `
      : css`
          position: sticky;
          justify-content: center;
          margin-top: 8px;
        `};

  ${({ showShadow, mode }) =>
    showShadow &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        box-shadow: ${({ theme }): string =>
          mode === 'fixed' ? theme?.kallisto?.shadow?.smallDownShadow : theme?.kallisto?.shadow?.mediumDownShadow};
        /* show the shadow only below the bar, extending slightly past the bottom corners */
        clip-path: inset(100% -4px -20px -4px);
      }
    `};

  ${({ mode }) =>
    whenDesktopLayoutIsNarrow(css`
      @media (max-width: 767px) {
        position: static;
        margin-top: 0;
        padding: 0 16px;

        &::after {
          content: none;
        }

        ${VerticalSeparator} {
          display: none;
        }
      }

      ${
        mode === 'fixed' &&
        css`
          @media (min-width: 768px) {
            position: static;
            margin-top: 8px;
            padding: 0;

            &::after {
              content: none;
            }
          }
        `
      }
    `)};
`;

export const HorizontalLayoutInnerContainer = styled.div<{ mode: 'fixed' | 'sticky' }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100%;

  ${({ mode }) =>
    mode === 'fixed'
      ? css`
          width: 100%;
          max-width: unset;
        `
      : css`
          width: 1200px;
          max-width: 100%;
          justify-content: flex-start;
          min-width: unset;
        `}
`;

export const StickyHeaderClipper = styled.div<{ brand: Brand }>`
  position: fixed;
  top: ${({ brand }) => headerHeightCss(HEADER_HEIGHT(brand, true))};
  left: 0;
  right: 0;
  height: 72px;
  background: white;
  z-index: 3;
`;
